import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MicrophoneIcon,
  StopIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useWorkbench } from '../WorkbenchContext';
import { processChildrenWithCitations } from './CaseReference';

const API_BASE_URL = '/api';

/**
 * Workbench Chat Interface - Legal Document Assistant
 *
 * Uses workspace model for true artifact collaboration:
 * - Documents stored as files in workspace (not just context)
 * - Surgical edits with diff review
 * - Citation validation from Reference Library
 */
function WorkbenchChatInterface({ isCollapsed, onToggleCollapse }) {
  const {
    activeCase,
    currentRequirementsDocument,
    setCurrentRequirementsDocument,
    artifactTitle,
    setArtifactTitle,
    requirementsOutputs,
    setRequirementsOutputs,
    setActiveTab,
    setSelectedReferenceCase
  } = useWorkbench();

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Session ID for ClaudeSDKClient conversation continuity
  // Generates a new session when case changes
  const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const [sessionId, setSessionId] = useState(generateSessionId);
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem('legal_workbenchChatWidth');
    return saved ? parseInt(saved, 10) : 420;
  });
  const [isResizing, setIsResizing] = useState(false);

  // Configuration state - like BEUSA
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [outputDocumentName, setOutputDocumentName] = useState('Legal Analysis');

  // Note: No more pendingEdit/diff review - edits are applied directly via workspace tools

  // Handle clicking on a case citation - navigate to Reference Library
  const handleCaseClick = (caseId) => {
    setSelectedReferenceCase(caseId);
    setActiveTab('references');
  };

  // Custom markdown components that process citations
  const markdownComponents = useMemo(() => ({
    p: ({ children, ...props }) => (
      <p {...props}>{processChildrenWithCitations(children, handleCaseClick)}</p>
    ),
    li: ({ children, ...props }) => (
      <li {...props}>{processChildrenWithCitations(children, handleCaseClick)}</li>
    ),
    strong: ({ children, ...props }) => (
      <strong {...props}>{processChildrenWithCitations(children, handleCaseClick)}</strong>
    ),
    em: ({ children, ...props }) => (
      <em {...props}>{processChildrenWithCitations(children, handleCaseClick)}</em>
    ),
    td: ({ children, ...props }) => (
      <td {...props}>{processChildrenWithCitations(children, handleCaseClick)}</td>
    ),
    th: ({ children, ...props }) => (
      <th {...props}>{processChildrenWithCitations(children, handleCaseClick)}</th>
    )
  }), []);

  // Combine case documents AND saved artifacts for Input Documents
  // De-duplicate by title to avoid showing same document twice
  const allInputDocuments = useMemo(() => {
    const seenTitles = new Set();
    const docs = [];

    // Add case documents first (they take priority)
    (activeCase?.documents || []).forEach(d => {
      const title = d.title || d.filename;
      if (!seenTitles.has(title)) {
        seenTitles.add(title);
        docs.push({
          ...d,
          source: 'case',
          displayIcon: d.type === 'sample' ? '\u{1F4C4}' : '\u{1F4CE}'
        });
      }
    });

    // Add artifacts (skip if title already exists)
    requirementsOutputs.forEach(a => {
      if (!seenTitles.has(a.title)) {
        seenTitles.add(a.title);
        docs.push({
          id: a.id,
          title: a.title,
          content: a.content,
          source: 'artifact',
          displayIcon: '\u{1F4DD}',
          createdAt: a.createdAt
        });
      }
    });

    return docs;
  }, [activeCase?.documents, requirementsOutputs]);

  // Check if an artifact is currently being edited
  const isCurrentlyEditing = (docId) => {
    const artifact = requirementsOutputs.find(a => a.id === docId);
    if (!artifact) return false;
    return currentRequirementsDocument === artifact.content && artifactTitle === artifact.title;
  };

  // Initialize selected documents when case/artifacts change
  useEffect(() => {
    if (activeCase?.documents?.length > 0) {
      // Select all case documents by default
      setSelectedDocuments(new Set(activeCase.documents.map(d => d.id)));
    } else {
      setSelectedDocuments(new Set());
    }
  }, [activeCase?.id, activeCase?.documents?.length]);

  // Note: Removed auto-sync of outputDocumentName -> artifactTitle
  // The title should only be set when content is actually saved/generated,
  // not when the user merely types a document name

  const handleDocumentToggle = (docId, checked) => {
    setSelectedDocuments(prev => {
      const updated = new Set(prev);
      if (checked) {
        updated.add(docId);
      } else {
        updated.delete(docId);
      }
      return updated;
    });
  };

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Session cleanup helper
  const closeSession = async (sid) => {
    if (sid) {
      try {
        await fetch(`${API_BASE_URL}/workbench/session/${sid}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      } catch (err) {
        console.warn('Failed to close session:', err);
      }
    }
  };

  // Cleanup session when component unmounts
  useEffect(() => {
    return () => {
      closeSession(sessionId);
    };
  }, [sessionId]);

  // Reset messages, close old session, and create new session when case changes
  const prevCaseIdRef = useRef(activeCase?.id);
  useEffect(() => {
    if (prevCaseIdRef.current !== activeCase?.id) {
      // Case changed - close old session, clear messages, generate new session
      closeSession(sessionId);
      setMessages([]);
      setSessionId(generateSessionId());
      prevCaseIdRef.current = activeCase?.id;
    }
  }, [activeCase?.id]); // Note: sessionId intentionally excluded to avoid infinite loop

  // Save width
  useEffect(() => {
    localStorage.setItem('legal_workbenchChatWidth', width.toString());
  }, [width]);

  // Handle resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = window.innerWidth - e.clientX;
        const clampedWidth = Math.max(360, Math.min(800, newWidth));
        setWidth(clampedWidth);
      }
    };

    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Get current workspace filename
  const getWorkspaceFilename = () => {
    const safeName = (artifactTitle || 'document')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return `${safeName}.md`;
  };

  // Ensure document is in workspace before editing (case-specific)
  const ensureDocumentInWorkspace = async () => {
    const filename = getWorkspaceFilename();

    if (currentRequirementsDocument && activeCase?.id) {
      try {
        // Use case-specific workspace endpoint
        await fetch(`${API_BASE_URL}/workspace/case/${activeCase.id}/${filename}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            content: currentRequirementsDocument,
            description: `Saved from ${artifactTitle}`
          })
        });
        return filename;
      } catch (error) {
        console.error('Error saving to workspace:', error);
      }
    }
    return filename;
  };

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);

    try {
      // Single unified endpoint - AI decides whether to use tools or respond conversationally
      await handleChat(text, userMessage);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        error: true
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Unified chat handler - AI decides tool usage based on user intent
  // Uses ClaudeSDKClient session-based approach (matches anthropic-sdk-demo)
  const handleChat = async (text, userMessage) => {
    // Ensure any open document is saved to workspace first
    let currentDocFilename = null;
    if (currentRequirementsDocument && activeCase?.id) {
      currentDocFilename = await ensureDocumentInWorkspace();
    }

    // Use new /workbench/chat endpoint with session_id for conversation continuity
    // Claude maintains context automatically via ClaudeSDKClient - no need to send history
    const response = await fetch(`${API_BASE_URL}/workbench/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message: text,
        session_id: sessionId,  // Session-based context (ClaudeSDKClient remembers conversation)
        case_id: activeCase?.id,
        selected_case_ids: activeCase?.linkedReferences || [],
        current_document: currentDocFilename,
        selected_documents: allInputDocuments.filter(d => selectedDocuments.has(d.id)),
        output_document_name: outputDocumentName  // User's intended output document name
      })
    });

    if (!response.ok) throw new Error('Failed to send message');

    // Stream response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedText = '';
    let messageIndex = messages.length + 1;

    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      /* eslint-disable no-loop-func */
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'text') {
              accumulatedText += data.content;
              setMessages(prev => {
                const updated = [...prev];
                updated[messageIndex] = {
                  ...updated[messageIndex],
                  content: accumulatedText
                };
                return updated;
              });
            } else if (data.type === 'tool_start') {
              // Show tool usage in message
              setMessages(prev => {
                const updated = [...prev];
                updated[messageIndex] = {
                  ...updated[messageIndex],
                  content: accumulatedText + `\n\n*Using ${data.tool}...*`
                };
                return updated;
              });
            } else if (data.type === 'tool_result') {
              // Clear tool indicator after result
              setMessages(prev => {
                const updated = [...prev];
                updated[messageIndex] = {
                  ...updated[messageIndex],
                  content: accumulatedText
                };
                return updated;
              });
            } else if (data.type === 'citation_warning') {
              // Show citation warning
              setMessages(prev => {
                const updated = [...prev];
                updated[messageIndex] = {
                  ...updated[messageIndex],
                  content: accumulatedText + `\n\n**Warning:** ${data.content}`,
                  citationWarning: data
                };
                return updated;
              });
            } else if (data.type === 'result' || data.done) {
              // Final update
              setMessages(prev => {
                const updated = [...prev];
                updated[messageIndex] = {
                  ...updated[messageIndex],
                  content: accumulatedText || 'Done.',
                  streaming: false
                };
                return updated;
              });

              // If document content was created/modified, update viewer
              if (data.content && data.workspace_file) {
                setCurrentRequirementsDocument(data.content);
                await saveToArtifacts(data.content);
              }

              // EXIT - stop processing to prevent hang
              return;
            } else if (data.type === 'error') {
              setMessages(prev => {
                const updated = [...prev];
                updated[messageIndex] = {
                  ...updated[messageIndex],
                  content: `Error: ${data.content}`,
                  streaming: false,
                  error: true
                };
                return updated;
              });
              return;
            }
          } catch (e) {
            console.error('Parse error:', e);
          }
        }
      }
      /* eslint-enable no-loop-func */
    }
  };

  // Save a document to workspace (case-specific)
  const saveToWorkspace = async (filename, content, description = '') => {
    if (!activeCase?.id) {
      console.error('No active case for saving to workspace');
      return false;
    }
    try {
      // Use case-specific workspace endpoint
      await fetch(`${API_BASE_URL}/workspace/case/${activeCase.id}/${filename}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content, description })
      });
      return true;
    } catch (error) {
      console.error('Error saving to workspace:', error);
      return false;
    }
  };

  // Note: Document creation/editing now handled via handleChat() using /workbench/chat endpoint

  const saveToArtifacts = async (content) => {
    // Prioritize outputDocumentName (user's explicit choice) over artifactTitle
    // This ensures the user's intended document name is used
    const title = outputDocumentName || artifactTitle || 'Untitled Document';

    // Now sync the title to artifactTitle (since we have actual content)
    if (!artifactTitle && outputDocumentName) {
      setArtifactTitle(outputDocumentName);
    }

    const savedArtifact = {
      id: Date.now().toString(),
      title,
      content,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      caseId: activeCase?.id,
      caseName: activeCase?.name
    };

    // 1. Update React state (for immediate UI update)
    const existingIndex = requirementsOutputs.findIndex(
      output => output.title === artifactTitle
    );

    if (existingIndex >= 0) {
      setRequirementsOutputs(prev => {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...savedArtifact };
        return updated;
      });
    } else {
      setRequirementsOutputs(prev => [savedArtifact, ...prev]);
    }

    // 2. ALSO save to backend workspace (for persistence)
    if (activeCase?.id) {
      const filename = getWorkspaceFilename();
      await saveToWorkspace(filename, content, `Artifact: ${artifactTitle}`);
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());

        // Process audio
        setIsProcessing(true);
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'audio.webm');

          const response = await fetch('/api/ai/speech-to-text', {
            method: 'POST',
            body: formData,
            credentials: 'include'
          });

          const data = await response.json();
          if (data.transcription) {
            // handleSendMessage will manage isProcessing from here
            handleSendMessage(data.transcription);
          } else {
            // No transcription - reset processing state
            setIsProcessing(false);
          }
        } catch (error) {
          console.error('Error processing audio:', error);
          setIsProcessing(false);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Context-aware suggestions
  const getSuggestions = () => {
    if (!activeCase) {
      return ['Select a case from My Cases to begin'];
    }

    if (!currentRequirementsDocument) {
      const hasDocuments = activeCase.documents?.length > 0;
      if (hasDocuments) {
        return [
          'What are the key legal issues in this case?',
          'Create a defense strategy outline based on the case documents',
          'Explain the elements of the charges',
          'Draft a motion to suppress evidence'
        ];
      }
      return [
        'What is consideration in contract law?',
        'Upload case documents to begin analysis',
        'Import a sample case from My Cases'
      ];
    }

    // Has document - show mix of questions and editing suggestions
    return [
      'What does this document say about damages?',
      'Add a section on potential counterarguments',
      'How should I strengthen this argument?',
      'Expand the legal analysis section with more detail'
    ];
  };

  // Collapsed view
  if (isCollapsed) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed bottom-6 right-6 z-50"
      >
        <button
          onClick={onToggleCollapse}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-galaxy-500 to-galaxy-700 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
          title="Open Legal Assistant"
        >
          <SparklesIcon className="w-6 h-6 text-white" />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ width: `${width}px` }}
      className="bg-galaxy-900/60 border-l border-galaxy-800/50 flex flex-col h-full relative"
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-galaxy-500/50 transition-colors ${
          isResizing ? 'bg-galaxy-500' : ''
        }`}
        style={{ touchAction: 'none' }}
      />

      {/* Header */}
      <div className="p-4 border-b border-galaxy-800/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-galaxy-500 to-galaxy-700 flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-white">Legal Document Assistant</h2>
            <p className="text-xs text-galaxy-400">
              {activeCase ? `Working on: ${activeCase.name}` : 'No case selected'}
            </p>
          </div>
          <button
            onClick={onToggleCollapse}
            className="p-1 text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded transition-colors"
            title="Minimize"
          >
            <ChevronDownIcon className="w-5 h-5" />
          </button>
        </div>

      </div>

      {/* Configuration Panel - Input Documents & Output Name */}
      {activeCase && (
        <div className="bg-galaxy-900/50 border-b border-galaxy-800/50 px-4 py-3 space-y-3">
          {/* Input Documents - Case Docs + Saved Artifacts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-galaxy-400 uppercase tracking-wide">
                Input Documents
              </span>
              <span className="text-xs text-galaxy-500">
                {selectedDocuments.size} of {allInputDocuments.length} selected
              </span>
            </div>
            {allInputDocuments.length > 0 ? (
              <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto pr-1">
                {/* Case Documents Section - uploaded/sample files */}
                {activeCase?.documents?.length > 0 && (
                  <div className="text-xs text-galaxy-500 uppercase tracking-wide px-2 pt-1 pb-0.5">
                    Source Files ({activeCase.documents.length})
                  </div>
                )}
                {allInputDocuments.filter(d => d.source === 'case').map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-2 px-2 py-1.5 bg-galaxy-800/30 hover:bg-galaxy-800/50 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDocuments.has(doc.id)}
                      onChange={(e) => handleDocumentToggle(doc.id, e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-galaxy-600 bg-galaxy-800 text-galaxy-500 focus:ring-galaxy-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-sm">{doc.displayIcon}</span>
                    <button
                      onClick={() => {
                        setCurrentRequirementsDocument(doc.content);
                        setArtifactTitle(doc.title || doc.filename);
                      }}
                      className="text-sm text-galaxy-300 hover:text-white truncate flex-1 text-left"
                      title="Click to view document"
                    >
                      {doc.title || doc.filename}
                    </button>
                  </div>
                ))}

                {/* Generated Documents Section - AI-created artifacts */}
                {requirementsOutputs.length > 0 && (
                  <div className="text-xs text-galaxy-500 uppercase tracking-wide px-2 pt-2 pb-0.5 border-t border-galaxy-700/50 mt-1">
                    Your Documents ({requirementsOutputs.length})
                  </div>
                )}
                {allInputDocuments.filter(d => d.source === 'artifact').map(doc => {
                  const editing = isCurrentlyEditing(doc.id);
                  return (
                    <div
                      key={doc.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${
                        editing
                          ? 'bg-green-900/30 border border-green-700/50'
                          : 'bg-galaxy-800/30 hover:bg-galaxy-800/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDocuments.has(doc.id)}
                        onChange={(e) => handleDocumentToggle(doc.id, e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-galaxy-600 bg-galaxy-800 text-galaxy-500 focus:ring-galaxy-500 focus:ring-offset-0 cursor-pointer"
                      />
                      <span className="text-sm">{doc.displayIcon}</span>
                      <button
                        onClick={() => {
                          setCurrentRequirementsDocument(doc.content);
                          setArtifactTitle(doc.title);
                        }}
                        className="text-sm text-galaxy-300 hover:text-white truncate flex-1 text-left"
                        title="Click to view document"
                      >
                        {doc.title}
                      </button>
                      {editing && (
                        <span className="px-1.5 py-0.5 text-xs bg-green-700/50 text-green-300 rounded">
                          Editing
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-galaxy-500 italic px-2 py-1.5">
                No documents available. Add documents in My Cases or create new ones.
              </div>
            )}
          </div>

          {/* Output Document Name */}
          <div>
            <label className="text-xs font-semibold text-galaxy-400 uppercase tracking-wide block mb-1.5">
              Output Document Name
            </label>
            <input
              type="text"
              value={outputDocumentName}
              onChange={(e) => setOutputDocumentName(e.target.value)}
              placeholder="Legal Analysis"
              className="w-full px-3 py-1.5 bg-galaxy-800/50 border border-galaxy-700/50 rounded text-sm text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
            />
          </div>

          {/* Linked References */}
          {activeCase.linkedReferences?.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-galaxy-400 uppercase tracking-wide block mb-1.5">
                Linked References ({activeCase.linkedReferences.length})
              </span>
              <div className="flex flex-wrap gap-1.5">
                {activeCase.linkedReferences.slice(0, 3).map(refId => (
                  <span
                    key={refId}
                    className="px-2 py-0.5 bg-galaxy-700/50 text-galaxy-300 text-xs rounded"
                  >
                    {refId.replace(/-/g, ' ').replace(/\d{4}$/, '').trim()}
                  </span>
                ))}
                {activeCase.linkedReferences.length > 3 && (
                  <span className="px-2 py-0.5 text-galaxy-500 text-xs">
                    +{activeCase.linkedReferences.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <SparklesIcon className="w-12 h-12 text-galaxy-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Legal Document Assistant</h3>
            <p className="text-sm text-galaxy-400 mb-6">
              Create and edit legal documents with AI assistance
            </p>

            {/* Suggestions */}
            <div className="space-y-2">
              {getSuggestions().map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSendMessage(suggestion)}
                  className="w-full text-left px-4 py-2 text-sm text-galaxy-300 bg-galaxy-800/30 hover:bg-galaxy-800/50 rounded-lg transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <div key={index} className="space-y-2">
            <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[90%] rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-galaxy-600 text-white'
                    : message.role === 'system'
                    ? 'bg-galaxy-700/50 text-galaxy-300 text-sm italic'
                    : message.error
                    ? 'bg-red-900/30 text-red-300 border border-red-700/50'
                    : 'bg-galaxy-800/50 text-galaxy-100'
                }`}
              >
                <div className="text-sm prose prose-sm prose-invert max-w-none">
                  {message.role === 'assistant' || message.role === 'system' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {message.content}
                    </ReactMarkdown>
                  ) : (
                    <span className="whitespace-pre-wrap">{message.content}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Note: Diff viewer removed - edits are applied directly via workspace tools */}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-galaxy-800/50 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm text-galaxy-400">Processing...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-galaxy-800/50">
        {/* Recording Indicator */}
        {isRecording && (
          <div className="mb-3 flex items-center justify-center gap-2 text-red-400">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 h-4 bg-red-400 rounded-full animate-pulse"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
            <span className="text-sm">Listening...</span>
          </div>
        )}

        {/* Processing Indicator - shows during speech-to-text and message processing */}
        {isProcessing && !isRecording && (
          <div className="mb-3 flex items-center justify-center gap-2 text-galaxy-400">
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm">Processing...</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Voice Input Button */}
          <button
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onMouseLeave={stopRecording}
            disabled={!activeCase}
            className={`p-3 rounded-full transition-colors ${
              isRecording
                ? 'bg-red-500 text-white'
                : !activeCase
                ? 'bg-galaxy-800/50 text-galaxy-600 cursor-not-allowed'
                : 'bg-galaxy-800 text-galaxy-400 hover:text-white hover:bg-galaxy-700'
            }`}
            title={activeCase ? 'Hold to speak' : 'Select a case first'}
          >
            {isRecording ? (
              <StopIcon className="w-5 h-5" />
            ) : (
              <MicrophoneIcon className="w-5 h-5" />
            )}
          </button>

          {/* Text Input */}
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputText)}
            placeholder={activeCase ? "Ask or instruct..." : "Select a case first"}
            disabled={!activeCase}
            className="flex-1 bg-galaxy-800/50 border border-galaxy-700/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />

          {/* Send Button */}
          <button
            onClick={() => handleSendMessage(inputText)}
            disabled={!inputText.trim() || isProcessing || !activeCase}
            className="p-3 bg-galaxy-600 text-white rounded-full hover:bg-galaxy-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <PaperAirplaneIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default WorkbenchChatInterface;
